import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { PieChart as PieChartContainer, Pie, Cell, Legend, Label } from 'recharts';
import { isEmpty } from 'ramda';
import { defaultNumberFormat } from '@cyberint/common-utils';
import CustomResponsiveContainer from '../CustomResponsiveContainer';
import ActiveSector from './components/ActiveSector';
import PieDefaultLegend from '../DefaultLegend';
import * as S from './styled';

// Pay attention - you must have name and value properties in your data if you want to use default ActiveSector
const EMPTY = {};
const EMPTY_ARR = [];
const PieChart = ({
  data = EMPTY_ARR,
  dataKey,
  innerRadius = 50,
  outerRadius = 60,
  cornerRadius,
  withLegend,
  onClick,
  ActiveSectorShape = ActiveSector,
  showTotal,
  emptyMessage,
  PieLegend = PieDefaultLegend,
  PieLabel,
  selectedIndex,
  partialSelectedIndex,
  spaceBetweenSectors = 1,
  isLoading,
  title,
  legendWrapperStyle = EMPTY,
  legendAlign = 'right',
  piePosition = {},
  onSectorHover
}) => {
  const [hoveredIndex, setHoverIndex] = useState(-1);

  useEffect(() => {
    onSectorHover?.(hoveredIndex);
  }, [hoveredIndex]);

  const setHoverHandler = useCallback((_, index) => {
    setHoverIndex(index);
  }, []);

  const removeHoverHandler = useCallback(() => {
    setHoverIndex(-1);
  }, []);

  const sectionClickHandler = useCallback(
    (item, index) => {
      if (typeof onClick !== 'function') return;
      const payload = item?.payload || data?.[index];
      if (!isEmpty(payload) && !payload?.isDisabled) {
        onClick(payload);
      }
    },
    [data, onClick]
  );

  const getActiveShape = useCallback(
    // eslint-disable-next-line react/jsx-props-no-spreading
    props => <ActiveSectorShape {...props} showTotal={showTotal} />,
    [ActiveSectorShape, showTotal]
  );

  const renderPieLegend = props => (
    <PieLegend
      {...props}
      selectedIndex={selectedIndex}
      partialSelectedIndex={partialSelectedIndex}
      hoverIndex={hoveredIndex}
      title={title}
    />
  );
  const total = showTotal && data?.reduce((acc, d) => acc + d.value, 0);

  return (
    <S.Wrapper>
      {isLoading ? (
        <S.StyledStack spacing={1} style={{ flexDirection: 'row' }}>
          <S.CircularSkeleton>
            <S.StyledSkeleton variant="circular" height="80%" />
          </S.CircularSkeleton>
          <S.LegendSkeletonContainer>
            {data?.map(() => (
              <S.StyledSkeleton variant="text" sx={{ fontSize: '1rem' }} />
            ))}
          </S.LegendSkeletonContainer>
        </S.StyledStack>
      ) : (
        <CustomResponsiveContainer width="100%" height="100%">
          <PieChartContainer onMouseLeave={removeHoverHandler}>
            {withLegend ? (
              <Legend
                layout="vertical"
                verticalAlign="middle"
                align={legendAlign}
                wrapperStyle={{ paddingLeft: '5px', ...legendWrapperStyle }}
                onClick={sectionClickHandler}
                onMouseEnter={setHoverHandler}
                content={renderPieLegend}
              />
            ) : null}
            <Pie
              data={data}
              dataKey={dataKey}
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              cornerRadius={cornerRadius}
              blendStroke
              fill="var(--baileyBells)"
              // start and end angle gives us clockwise direction for the chart rendering animation
              startAngle={90}
              endAngle={-270}
              cx={piePosition.cx || '50%'}
              cy={piePosition.cy || '50%'}
              dx={outerRadius}
              activeIndex={hoveredIndex}
              activeShape={getActiveShape}
              onMouseEnter={setHoverHandler}
              onMouseLeave={removeHoverHandler}
              paddingAngle={spaceBetweenSectors}
              onClick={sectionClickHandler}>
              {data?.map((entry, i) => (
                <Cell key={entry?.name || i} fill={entry?.color || S.PIE_COLORS[i % S.PIE_COLORS.length]} />
              ))}
              {!!showTotal && (
                <Label
                  value={defaultNumberFormat(total)}
                  position="center"
                  fill="var(--pie-total-color)"
                  fontSize={14}
                  content={PieLabel}
                />
              )}
            </Pie>
          </PieChartContainer>
        </CustomResponsiveContainer>
      )}
      {total === 0 && !isLoading && emptyMessage && <S.EmptyPlaceHolder>{emptyMessage}</S.EmptyPlaceHolder>}
    </S.Wrapper>
  );
};

PieChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  dataKey: PropTypes.string.isRequired,
  chartMargin: PropTypes.shape({}),
  innerRadius: PropTypes.number,
  outerRadius: PropTypes.number,
  cornerRadius: PropTypes.number,
  withLegend: PropTypes.bool,
  showTotal: PropTypes.bool,
  emptyMessage: PropTypes.node,
  onClick: PropTypes.func,
  ActiveSectorShape: PropTypes.elementType,
  PieLegend: PropTypes.elementType,
  PieLabel: PropTypes.elementType,
  isNoDataMode: PropTypes.bool,
  selectedIndex: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.number), PropTypes.number]),
  partialSelectedIndex: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.number), PropTypes.number]),
  spaceBetweenSectors: PropTypes.number,
  isLoading: PropTypes.bool,
  title: PropTypes.string,
  legendWrapperStyle: PropTypes.object,
  legendAlign: PropTypes.string,
  onSectorHover: PropTypes.func,
  piePosition: PropTypes.shape({
    cx: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    cy: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })
};

export default PieChart;
